import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { Button, Card, Form, Input, InputNumber, Switch, Tabs, Typography, message } from 'antd'
import { adminApi } from '../../api'
import type { AiSkillRow } from './AgentSkillList'
import './AgentSettings.css'

export function AgentSkillEditor() {
  const { skillId: skillIdParam } = useParams()
  const isNew = skillIdParam === 'new' || skillIdParam == null
  const skillId = isNew ? null : Number(skillIdParam)
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const codeTimer = useRef<number | null>(null)

  const loadSkill = useCallback(() => {
    if (!skillId || !Number.isFinite(skillId)) return Promise.resolve()
    setLoading(true)
    return adminApi
      .get<AiSkillRow & { content_zh_cn: string; content_en: string; content_zh_tw: string }>(
        `/admin/agents/skills/${skillId}`,
      )
      .then((r) => form.setFieldsValue(r.data))
      .catch((e) => {
        message.error(e.message)
        navigate('/manage/agents/skills', { replace: true })
      })
      .finally(() => setLoading(false))
  }, [skillId, form, navigate])

  useEffect(() => {
    if (isNew) {
      form.setFieldsValue({
        sort_no: 0,
        is_active: true,
        content_zh_cn: '',
        content_en: '',
        content_zh_tw: '',
      })
      return
    }
    void loadSkill()
  }, [isNew, loadSkill, form])

  const suggestCode = (name: string) => {
    const trimmed = name.trim()
    if (!trimmed) {
      form.setFieldValue('skill_code', '')
      return
    }
    adminApi
      .get<{ skill_code: string }>('/admin/agents/skills/suggest-code', { params: { name: trimmed } })
      .then((r) => form.setFieldValue('skill_code', r.data.skill_code))
      .catch(() => undefined)
  }

  const onNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!isNew) return
    const name = e.target.value
    if (codeTimer.current) window.clearTimeout(codeTimer.current)
    codeTimer.current = window.setTimeout(() => suggestCode(name), 300)
  }

  const save = () => {
    void form.validateFields().then((v) => {
      setSaving(true)
      const body = {
        skill_name: v.skill_name,
        description: v.description || '',
        content_zh_cn: v.content_zh_cn || '',
        content_en: v.content_en || '',
        content_zh_tw: v.content_zh_tw || '',
        sort_no: v.sort_no ?? 0,
        is_active: v.is_active ?? true,
        ...(isNew ? { skill_code: v.skill_code || undefined } : {}),
      }
      const req = isNew
        ? adminApi.post<AiSkillRow>('/admin/agents/skills', body)
        : adminApi.put<AiSkillRow>(`/admin/agents/skills/${skillId}`, body)
      return req
        .then((r) => {
          message.success('已保存')
          if (isNew) navigate(`/manage/agents/skills/${r.data.id}`, { replace: true })
        })
        .catch((e) => message.error(e.message))
        .finally(() => setSaving(false))
    })
  }

  if (!isNew && (!Number.isFinite(skillId) || (skillId ?? 0) <= 0)) {
    return (
      <div className="agent-editor">
        <Typography.Text type="secondary">无效的 Skill ID</Typography.Text>
      </div>
    )
  }

  return (
    <div className="agent-editor">
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        style={{ paddingLeft: 0, marginBottom: 8 }}
        onClick={() => navigate('/manage/agents/skills')}
      >
        返回 Skill 库
      </Button>

      <Card title={isNew ? '新建 Skill' : '编辑 Skill'} loading={loading} style={{ maxWidth: 960 }}>
        <Form form={form} layout="vertical">
          <Form.Item name="skill_name" label="名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="如 简洁回答风格" onChange={onNameChange} />
          </Form.Item>
          <Form.Item
            name="skill_code"
            label="标识"
            extra={isNew ? '插入智能体时使用 {{skill:标识}}' : '标识保存后不可修改'}
          >
            <Input disabled placeholder="保存前自动生成" />
          </Form.Item>
          <Form.Item name="description" label="说明">
            <Input.TextArea rows={2} placeholder="适用场景（可选）" />
          </Form.Item>

          <Typography.Text strong>片段正文</Typography.Text>
          <Tabs
            className="agent-editor-prompt-tabs"
            items={[
              {
                key: 'zh-CN',
                label: '简体中文',
                children: (
                  <Form.Item name="content_zh_cn" style={{ marginBottom: 0 }}>
                    <Input.TextArea rows={12} placeholder="Skill 正文（简体）" />
                  </Form.Item>
                ),
              },
              {
                key: 'en',
                label: 'English',
                children: (
                  <Form.Item name="content_en" style={{ marginBottom: 0 }}>
                    <Input.TextArea rows={12} placeholder="Skill content (English)" />
                  </Form.Item>
                ),
              },
              {
                key: 'zh-TW',
                label: '繁體中文',
                children: (
                  <Form.Item name="content_zh_tw" style={{ marginBottom: 0 }}>
                    <Input.TextArea rows={12} placeholder="Skill 正文（繁體）" />
                  </Form.Item>
                ),
              },
            ]}
          />

          <Form.Item name="sort_no" label="排序" style={{ marginTop: 16, maxWidth: 200 }}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="is_active" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Button type="primary" onClick={save} loading={saving}>
            保存
          </Button>
        </Form>
      </Card>
    </div>
  )
}
